import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useLazyQuery } from '@apollo/client';
import { GET_SERIES_CLASSIFICATION } from 'modules/Apollo/queries';
import { steppedHPConfigurations, makeVersionedSteps } from '../state';
import type { VersionedStepConfigurations } from '../state';
import type { GetSeriesClassificationQuery } from 'generated/graphql';
import { flattenSeriesClassification } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';

type ClassifyRequest = {
  viewportId: string;
  stepIndex: number;
  seriesDescription: string;
  studyDescription?: string;
};

type PendingClassification = {
  stepIndex: number;
  abort: AbortController;
};

export function useClinicalViewportClassification() {
  // `pendingClassificationsCount` is a React state counter so components re-render when
  // the in-flight count changes. `pendingRef` is a ref (not state) because the Map must
  // be mutated synchronously in the same microtask as the fetch, and making it state
  // would cause a stale-closure read of the old Map on rapid consecutive classify() calls.
  const [pendingClassificationsCount, setPendingClassificationsCount] = useState(0);
  const pendingRef = useRef<Map<string, PendingClassification>>(new Map());
  const setSteppedHP = useSetRecoilState(steppedHPConfigurations);

  const [fetchClassification] = useLazyQuery<GetSeriesClassificationQuery>(
    GET_SERIES_CLASSIFICATION,
    {
      context: { telemetry: { component: 'useClinicalViewportClassification' } },
    }
  );

  const classify = useCallback(
    (request: ClassifyRequest) => {
      const { viewportId, stepIndex, seriesDescription, studyDescription } = request;
      const key = `${stepIndex}:${viewportId}`;

      // Cancel existing classification for this viewport
      const existing = pendingRef.current.get(key);
      if (existing) {
        existing.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }

      const abortController = new AbortController();
      pendingRef.current.set(key, { stepIndex, abort: abortController });
      setPendingClassificationsCount((n) => n + 1);

      fetchClassification({
        variables: {
          dicomSeriesDescription: seriesDescription,
          dicomStudyDescription: studyDescription ?? undefined,
        },
        context: { fetchOptions: { signal: abortController.signal } },
      })
        .then((result) => {
          if (abortController.signal.aborted) return;

          const classification = result.data?.seriesClassification?.classification;
          if (classification) {
            const criteria = flattenSeriesClassification(classification).map((c) => ({
              category: c.category,
              value: c.value,
            }));

            setSteppedHP((prev: VersionedStepConfigurations) => {
              const steps = prev.steps?.map((step, i) => {
                if (i !== stepIndex) return step;
                return {
                  ...step,
                  viewports: {
                    ...step.viewports,
                    [viewportId]: {
                      ...step.viewports[viewportId],
                      criteria,
                    },
                  },
                };
              });
              return makeVersionedSteps(steps ?? null, prev);
            });
          }
        })
        .catch(() => {
          // Classification failed — viewport saves without criteria
        })
        .finally(() => {
          // Skip cleanup if cancelled or superseded — the replacement manages its own counter.
          if (!abortController.signal.aborted) {
            pendingRef.current.delete(key);
            setPendingClassificationsCount((n) => Math.max(0, n - 1));
          }
        });
    },
    [fetchClassification, setSteppedHP]
  );

  const cancelForStep = useCallback((stepIndex: number) => {
    for (const [key, pending] of pendingRef.current) {
      if (pending.stepIndex === stepIndex) {
        pending.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }
    }
  }, []);

  const cancelAll = useCallback(() => {
    for (const [, pending] of pendingRef.current) {
      pending.abort.abort();
    }
    pendingRef.current.clear();
    setPendingClassificationsCount(0);
  }, []);

  // Cancel all in-flight requests on unmount to prevent stale Recoil writes
  // after the component has been removed from the tree.
  useEffect(() => () => cancelAll(), [cancelAll]);

  return {
    classify,
    pendingClassifications: pendingClassificationsCount,
    cancelForStep,
    cancelAll,
  };
}
